import { nsMatrix } from "@/Namespaces";
import { CustomDateSourceData } from "@/ServerTypes/Data/CustomDateSourceData";
import { DataReportsService } from "@/ServerTypes/Data/DataReportsService";
import { ListItemRow } from "@/ServerTypes/Data/ListItemRow";
import { DateEditor, Fluent, IReadOnly, IStringValue, IValidateRequired, Widget, addOption, clearOptions, findElementWithRelativeId, formatDate, getjQuery, isTrimmedEmpty, localText, stringFormat, trimToNull, tryGetWidget, EditorUtils } from "@serenity-is/corelib";

export class CustomDateEditor extends Widget<CustomDateEditorOptions> implements IReadOnly, IStringValue, IValidateRequired {
    static override[Symbol.typeInfo] = this.registerEditor(nsMatrix, [IReadOnly, IStringValue]);

    static override createDefaultElement() { return document.createElement("select"); }

    private input: Fluent;
    private static readonly defaultInstanceCodeFields = ["instance_code", "InstanceCode", "instanceCode", "instance"];
    private static readonly initDelayMs = 75;

    constructor(props: { element?: any } & CustomDateEditorOptions) {
        super(props);

        this.element.addClass('kind');
        clearOptions(this.element);
        addOption(this.element, "", "Date");
        for (var k of (ListItemRow as any).getLookup()
            .items.filter(x => x.ListCode == "REP_PARM_CUSTOM_DATES"))
            addOption(this.element, k.ListItemCode, k.ListItemText);

        this.input = Fluent("input").class('dateQ s-CustomDateEditor').insertAfter(this.element);

        getjQuery()?.(this.input.getNode())?.datepicker?.(<any>{
            showOn: 'button',
            beforeShow: () => {
                return !this.element.matches('[disabled]') && !this.element.val();
            }
        });

        this.input.on('keyup.' + this.uniqueName, (e: any) => {
            if (e.which === 32 && !this.input.attr('readonly')) {
                this.set_value(formatDate(new Date()));
            }
            else {
                DateEditor.dateInputKeyup(e);
            }
        });

        this.element.on('change.' + this.uniqueName, () => {
            if (this.element.val()) {
                this.input.val('');
                this.setCalculatedDate();
            }

            this.updateRequired();
            this.updateInputReadOnly();

        });

        this.updateInputReadOnly();

        this.input.on('change.' + this.uniqueName, DateEditor.dateInputChange);

        this.addValidationRule(this.uniqueName, e => {
            if (this.element.val() || isTrimmedEmpty(this.input.val()))
                return null;

            var value = formatDate(this.input.val(), 'yyyy-MM-dd');
            if (this.options.minValue && value < this.options.minValue) {
                return stringFormat(localText('Validation.MinDate'), formatDate(this.options.minValue));
            }
            if (this.options.maxValue && value > this.options.maxValue) {
                return stringFormat(localText('Validation.MaxDate'), formatDate(this.options.maxValue));
            }
        });

        this.options.minValue = this.options.minValue || '1753-01-01';
        this.options.maxValue = this.options.maxValue || '9999-12-31';

        window.setTimeout(() => {
            this.bindInstanceChangeHandlers();
        }, CustomDateEditor.initDelayMs);
    }

    get_required(): boolean {
        return this.element.hasClass('required') || this.input.hasClass('required');
    }

    set_required(value: boolean): void {
        this.element.toggleClass('required', !!value);
        this.input.toggleClass('required', !!value);
    }

    setCalculatedDate() {
        var kind = this.element.val();

        if (!kind || !this.options.sourceData)
            return;

        var instanceCode = trimToNull(this.options.instanceCode) ?? this.getInstanceCodeFromRelativeField();
        if (!instanceCode) {
            this.input.val('');
            return;
        }

        DataReportsService.GetCustomDate({
            InstanceCode: instanceCode,
            Kind: kind,
            SourceData: this.options.sourceData
        }, response => {
            this.input.val(formatDate(response.Date));
        }, {
            onError: () => {
                return true;
            }
        });
    }

    private getInstanceCodeFieldNames() {
        let list : string | string[] = this.options.instanceCodeProperty;
        if (typeof list === "string" && list.includes(","))
            list = list.split(',').map(x => x.trim());

        if (Array.isArray(list))
            return list;
        if (typeof list === "string" && list.length)
            return [list];
        return CustomDateEditor.defaultInstanceCodeFields;
    }

    private getInstanceCodeFromRelativeField() {
        for (const field of this.getInstanceCodeFieldNames()) {
            const el = Fluent(findElementWithRelativeId(this.element, field));
            const value = el.length ? trimToNull(el.val()) : null;
            if (value)
                return value;
        }

        return this.getInstanceCodeFromWidgetHeader();
    }

    private getInstanceCodeFromWidgetHeader() {
        const header = this.element.closest(".s-WidgetHeader");
        if (!header.length)
            return null;

        const fieldTokens = this.getInstanceCodeFieldNames().map(trimToNull).filter(Boolean);

        for (const node of header.findAll(".param-on-widget .editor")) {
            if (node === this.domNode)
                continue;

            const el = Fluent(node);
            const token = trimToNull(el.attr("data-param-name")) ?? "";
            if (!token.length)
                continue;

            if (!fieldTokens.some(x => token.indexOf(x) >= 0))
                continue;

            const widget = tryGetWidget(node, Widget);
            const value = trimToNull(widget ? EditorUtils.getValue(widget) : el.val());
            if (value)
                return value;
        }

        return null;
    }

    private bindInstanceChangeHandlers() {
        for (const field of this.getInstanceCodeFieldNames()) {
            const elInstanceCode = findElementWithRelativeId(this.element, field);
            if (elInstanceCode)
                Fluent.on(elInstanceCode, "change", () => this.setCalculatedDate());
        }

        const header = this.element.closest(".s-WidgetHeader");
        if (!header.length)
            return;

        const normalize = (s: string) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
        const fieldTokens = this.getInstanceCodeFieldNames().map(normalize).filter(x => x.length);

        for (const node of header.findAll(".param-on-widget .editor")) {
            if (node === this.domNode)
                continue;

            const token = normalize(trimToNull(Fluent(node).attr("data-param-name")) ?? "");
            if (!token.length || !fieldTokens.some(x => token.indexOf(x) >= 0))
                continue;

            Fluent.on(node, "change", () => this.setCalculatedDate());
        }
    }

    get_readOnly() {
        return !!this.element.attr('disabled');
    }

    updateInputReadOnly() {
        if (this.get_readOnly() || this.element.val()) {
            this.input.addClass('readonly').attr('readonly', 'readonly');
            this.input.nextSibling('.ui-datepicker-trigger').style(s => s.opacity = '0.1');
        }
        else {
            this.input.removeAttr('readonly').removeClass('readonly');
            this.input.nextSibling('.ui-datepicker-trigger').style(s => s.opacity = '1');
        }
    }

    updateRequired() {
        if (this.element.val()) {
            if (this.input.hasClass("required"))
                this.input.removeClass("required");

            if (!this.element.hasClass("required"))
                this.element.addClass("required");
        } else {
            if (!this.input.hasClass("required"))
                this.input.addClass("required");

            if (this.element.hasClass("required"))
                this.element.removeClass("required");
        }
    }

    set_readOnly(value) {
        if (value != this.get_readOnly()) {
            if (value)
                this.element.attr('disabled', 'disabled');
            else
                this.element.removeAttr('disabled');

            this.updateInputReadOnly();
        }
    }

    addDashes(value: string) {
        if (value && value.length == 8)
            return value.substring(0, 4) + '-' + value.substring(4, 6) + '-' + value.substring(6, 8);

        return value;
    }

    get_value() {
        var value = this.element.val();
        if (value)
            return value;

        value = trimToNull(this.input.val());
        if (!value)
            return null;

        return formatDate(value, 'yyyyMMdd');
    }

    set_value(value: string) {
        let shouldCalculate = false;

        if (!value) {
            this.element.val('');
            this.input.val('');
        }
        else if (value.toLowerCase() === 'today' || value.toLowerCase() === 'now') {
            this.element.val('');
            this.input.val(formatDate(new Date()));
        }
        else if (this.element.children('option').filter((e) => Fluent(e).attr('value').toLowerCase() == value.toLowerCase()).length) {
            this.element.val(value);
            this.input.val('');
            shouldCalculate = true;
        }
        else {
            this.element.val('');
            this.input.val(formatDate(this.addDashes(value), null));
        }

        this.updateRequired()
        this.updateInputReadOnly();

        if (shouldCalculate) {
            window.setTimeout(() => {
                this.setCalculatedDate();
            }, CustomDateEditor.initDelayMs);
        }
    }
}

export interface CustomDateEditorOptions {
    minValue?: string;
    maxValue?: string;
    sourceData?: CustomDateSourceData;
    instanceCode?: string;
    instanceCodeProperty?: string | string[];
}
